import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { NotificationContext } from '../../context/NotificationContext';
import { UserContext } from '../../context/UserContext';
import { BASE_URL } from '../../utils/config';
import { apiFetch } from '../../utils/apiClient';

const LogViewer = () => {
  const [logContent, setLogContent] = useState('Loading log...');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addNotification } = useContext(NotificationContext);
  const { user } = useContext(UserContext);
  const [loadingFullLog, setLoadingFullLog] = useState(false);
  const [logFiles, setLogFiles] = useState([]);
  const [selectedLogFile, setSelectedLogFile] = useState('');
  const logContainerRef = useRef(null);

  const fetchLogFiles = useCallback(async () => {
    try {
      const response = await apiFetch(`${BASE_URL}/api/admin/logs`, {}, user.token);
      if (!response.ok) {
        throw new Error('Failed to fetch log files list.');
      }
      const files = await response.json();
      setLogFiles(files);
      if (files.length > 0 && !selectedLogFile) {
        setSelectedLogFile(files[0]); // Select the most recent file by default only on initial load
      }
    } catch (err) {
      addNotification(`Error fetching log files: ${err.message}`, 'error');
    }
  }, [user.token, addNotification, selectedLogFile]);

  const fetchLogContent = useCallback(
    async (full = false) => {
      if (!selectedLogFile) return;

      if (full) {
        setLoadingFullLog(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const url = new URL(`${BASE_URL}/api/admin/log`);
        url.searchParams.append('fileName', selectedLogFile);
        if (full) {
          url.searchParams.append('fullLog', 'true');
        }

        const response = await apiFetch(url.toString(), {}, user.token);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch log content.');
        }

        const text = await response.text();
        setLogContent(text);
      } catch (err) {
        setError(err.message);
        setLogContent(`Error: ${err.message}`);
        addNotification(`Error fetching log: ${err.message}`, 'error');
      } finally {
        if (full) setLoadingFullLog(false);
        else setIsLoading(false);
      }
    },
    [user.token, addNotification, selectedLogFile]
  );

  useEffect(() => {
    fetchLogFiles();
  }, [fetchLogFiles]);

  useEffect(() => {
    if (selectedLogFile) {
      fetchLogContent(false);
    }
  }, [selectedLogFile, fetchLogContent]);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logContent]);

  const renderLogContent = (content) => {
    if (typeof content !== 'string') {
      return content;
    }
    return content.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        <span
          style={{
            color: line.toLowerCase().includes('error') ? 'red' : line.toLowerCase().includes('warn') || line.toLowerCase().includes('warning') ? 'orange' : 'inherit',
          }}
        >
          {line}
        </span>
        <br />
      </React.Fragment>
    ));
  };

  return (
    <div style={{ padding: '10px 20px', maxWidth: '100%', margin: 'auto', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 12vh - 30px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h2 style={{ margin: 0 }}>Server Log Viewer</h2>
          <select value={selectedLogFile} onChange={(e) => setSelectedLogFile(e.target.value)} style={{ padding: '8px', fontSize: '1em' }}>
            {logFiles.map((file) => (
              <option key={file} value={file}>
                {file}
              </option>
            ))}
          </select>
        </div>
        <div>
          <button onClick={() => fetchLogContent(true)} disabled={loadingFullLog} style={{ padding: '8px 16px', cursor: 'pointer', marginRight: '10px' }}>
            {loadingFullLog ? 'Loading Full Log...' : 'Load Full Log'}
          </button>
          <button onClick={() => fetchLogContent(false)} disabled={isLoading} style={{ padding: '8px 16px', cursor: 'pointer' }}>
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      {error && <div style={{ color: 'red', marginBottom: '10px', flexShrink: 0 }}>{error}</div>}

      <div
        ref={logContainerRef}
        style={{
          backgroundColor: '#f4f4f4',
          border: '1px solid #ddd',
          padding: '15px',
          fontSize: '0.85em',
          wordBreak: 'break-all',
          flexGrow: 1, // Allow this div to grow and fill available space
          overflowY: 'auto', // Use auto for scrollbar
          borderRadius: '4px',
          fontFamily: 'monospace',
        }}
      >
        {isLoading && logContent === 'Loading log...' && !error ? <p>Loading log content...</p> : renderLogContent(logContent)}
      </div>
    </div>
  );
};

export default LogViewer;
